<template>
  <div class="space-y-4">
    <div class="flex justify-end">
      <Button
        label="Save"
        :disabled="!hasChanges"
        :loading="isSaving"
        data-testid="save-permissions"
        @click="save"
      />
    </div>
    <div class="rounded-lg border border-gray-100 bg-gray-50 p-6">
      <StaffPermissionsFields
        v-model:mobile="mobile"
        v-model:admin="admin"
        v-model:payments="payments"
        :hide-payments="!canGrantPayments"
        :instructor-note="isInstructor"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { ProfileRole, type AdminProfile } from "@/api/model";
import StaffPermissionsFields from "./StaffPermissionsFields.vue";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { useUpdateUser } from "../composables/useUpdateUser";
import { useCoachManagement } from "../composables/useCoachManagement";

const props = defineProps<{ user: AdminProfile; organizationId: number }>();
const emit = defineEmits<{ "refresh-user": [] }>();

const toast = useToast();
const { updateUser } = useUpdateUser();
const { makeCoach, removeCoach } = useCoachManagement();
// Only a payments admin may grant or revoke payments (the backend 403s otherwise).
const canGrantPayments = computed(() => useAuthStore().canManagePayments);

const isInstructor = computed(() =>
  Boolean(props.user.roles?.includes(ProfileRole.instructor))
);

const mobile = ref(false);
const admin = ref(false);
const payments = ref(false);
const isSaving = ref(false);

watch(
  () => props.user,
  (user) => {
    mobile.value = Boolean(user.is_coach);
    admin.value = Boolean(user.is_admin);
    payments.value = Boolean(user.can_manage_payments);
  },
  { immediate: true }
);

const paymentsChanged = computed(
  () =>
    canGrantPayments.value &&
    payments.value !== Boolean(props.user.can_manage_payments)
);
const hasChanges = computed(
  () =>
    mobile.value !== Boolean(props.user.is_coach) ||
    admin.value !== Boolean(props.user.is_admin) ||
    paymentsChanged.value
);

async function save() {
  isSaving.value = true;
  // Two writes (coach record, then profile PATCH) are not atomic: once the first
  // has landed, reload the user even if the second fails so the tab is not stale.
  let saved = false;
  try {
    // Mobile = the coach record; the coach API creates/archives it and the
    // backend recomputes the roles array itself.
    if (mobile.value !== Boolean(props.user.is_coach)) {
      const ok = mobile.value
        ? (await makeCoach(props.organizationId, props.user.id)).success
        : await removeCoach(props.organizationId, props.user.coach?.id ?? 0);
      if (!ok) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to update mobile access",
          life: 5000,
        });
        return;
      }
      saved = true;
    }
    if (admin.value !== Boolean(props.user.is_admin) || paymentsChanged.value) {
      const ok = await updateUser(
        props.user.id,
        canGrantPayments.value
          ? { is_admin: admin.value, can_manage_payments: payments.value }
          : { is_admin: admin.value },
        props.organizationId
      );
      if (!ok) return;
      saved = true;
    }
  } finally {
    isSaving.value = false;
    if (saved) emit("refresh-user");
  }
}
</script>
